/**
 * Entity scope (_Org.md: one entity · selected entities), on every screen of an Org and serialised into the URL so a
 * view is linkable and survives reloads.
 *
 * **There is no *Combined* in the app any more** (R4, A7; Roadmap: *Combined* and `/group` leave the app, and
 * `org.group` stays for the assistant). A screen opens on one company: the Org's own, found by the organisation
 * number the Org was made from, or — for an Org made before profiles that has not said which company it is — the
 * first managed entity. `?scope=combined` in a saved or linked address names nothing any more, and lands on that
 * default like an address with no scope at all, rather than on an error.
 */
import type { MessageKey } from '@autho/i18n';
import { formatOrgnr } from '@autho/i18n';

export type EntityScope = { mode: 'one'; id: string } | { mode: 'selected'; ids: string[] };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The scope an address names, or null when it names none: absent, malformed, or the retired `combined`. */
export function parseScope(value: unknown): EntityScope | null {
  if (typeof value !== 'string') return null;
  if (value.startsWith('one.')) {
    const id = value.slice(4);
    return UUID.test(id) ? { mode: 'one', id } : null;
  }
  if (value.startsWith('sel.')) {
    const ids = value
      .slice(4)
      .split(',')
      .filter((id) => UUID.test(id));
    return ids.length > 0 ? { mode: 'selected', ids } : null;
  }
  return null;
}

export function serializeScope(scope: EntityScope): string {
  switch (scope.mode) {
    case 'one':
      return `one.${scope.id}`;
    case 'selected':
      return `sel.${scope.ids.join(',')}`;
  }
}

/**
 * The scope a screen opens on when the address names none: the Org's own company, by the organisation number the
 * Org carries, else the first managed entity in the list. Null when there is neither — a fresh Org whose company
 * has not been added yet, or one that holds only external entities.
 */
export function defaultScope(
  entities: readonly { id: string; orgnr: string; state: string }[],
  orgnr: string | null,
): EntityScope | null {
  const own = orgnr === null ? undefined : entities.find((e) => e.orgnr === orgnr);
  const first = own ?? entities.find((e) => e.state === 'managed');
  return first ? { mode: 'one', id: first.id } : null;
}

export function inScope(scope: EntityScope | null, entity: { id: string }): boolean {
  if (scope === null) return false;
  switch (scope.mode) {
    case 'one':
      return entity.id === scope.id;
    case 'selected':
      return scope.ids.includes(entity.id);
  }
}

export function scopedIds(
  scope: EntityScope | null,
  entities: readonly { id: string }[],
): Set<string> {
  return new Set(entities.filter((e) => inScope(scope, e)).map((e) => e.id));
}

/**
 * **The company in view is chosen on the pages, and said on the screens that follow it** (F356; Thomas,
 * 2026-10-11: the switchers list only real profiles, Personal first, then the company profiles; a company inside a
 * profile is not a profile of its own, and does not belong in a switcher until it gets one in R5).
 *
 * Until then a second switcher, top-left, chose the company in view and named it, while the banner's switcher named
 * the profile — so with the profile's second company in view the two disagreed, and a company's page drew the
 * company in its address whatever either said. Now there is one switcher, of profiles. The scope stays what it was
 * — one company, or several, in the address, kept from screen to screen, the profile's own company when none is
 * named — and a company is put in view by opening it: its page, from Structure's table or the palette, carries it
 * as the scope (`scopeOnCompanyPage`). A screen that shows one company's records says which, in a sentence on the
 * screen (`InViewNote`) rather than a control in the banner.
 */

/** What is in view, as the sentence on a screen says it. */
export type InView =
  /** No company to put in view: an Org whose company is not added yet, or one holding only external entities. */
  | { mode: 'none' }
  /** One company. `own` when it is the company the profile was made from, by its organisation number. */
  | { mode: 'one'; id: string; name: string; orgnr: string; own: boolean }
  /** One company the profile's list does not hold: an address naming a company that is not, or is no longer, there. */
  | { mode: 'unlisted' }
  | { mode: 'several'; count: number };

export function inView(
  scope: EntityScope | null,
  entities: readonly { id: string; name: string; orgnr: string }[],
  ownOrgnr: string | null,
): InView {
  if (scope === null) return { mode: 'none' };
  if (scope.mode === 'selected') return { mode: 'several', count: scope.ids.length };
  const entity = entities.find((e) => e.id === scope.id);
  if (!entity) return { mode: 'unlisted' };
  return {
    mode: 'one',
    id: entity.id,
    name: entity.name,
    orgnr: entity.orgnr,
    own: ownOrgnr !== null && entity.orgnr === ownOrgnr,
  };
}

/** Words to show: a message and its values. */
export interface Said {
  key: MessageKey;
  values?: Record<string, string | number>;
}

/** The words themselves, in the reader's language. */
export function say(
  t: (key: MessageKey, values?: Record<string, string | number>) => string,
  said: Said,
): string {
  return t(said.key, said.values);
}

/**
 * How a screen uses the company in view, by its address. **follows**: it shows the records of the company in view —
 * the default one included — so it names it whenever the profile holds more than one. **narrows**: it shows the
 * whole profile unless a company was put in view, and then says so and offers the whole profile back (Clarify, F335).
 * Null for every other screen: the audit log shows the whole profile until its own filter narrows it (F361), Structure
 * lists every company and marks the one in view, and a company's own page names its company in its heading.
 */
export type ScopeUse = 'follows' | 'narrows';

const USES: Readonly<Record<string, ScopeUse>> = {
  '/documents': 'follows',
  '/counterparties': 'follows',
  '/bank': 'follows',
  '/beliefs': 'follows',
  '/clarify': 'narrows',
};

export function scopeUse(pathname: string): ScopeUse | null {
  return USES[pathname.replace(/\/+$/, '')] ?? null;
}

/** The sentence a screen opens with: which company it shows, or how many. Null when there is nothing to name. */
export function inViewWords(view: InView): Said | null {
  switch (view.mode) {
    case 'one':
      return {
        key: 'scope.inView.one',
        values: { name: view.name, orgnr: formatOrgnr(view.orgnr) },
      };
    case 'several':
      return { key: 'scope.inView.several', values: { count: view.count } };
    case 'none':
    case 'unlisted':
      return null;
  }
}

/**
 * **A company's own page is the company in view.** The scope its address must carry — that company alone — or null
 * when it already does, and for an id that names no company, which the page itself refuses.
 */
export function scopeOnCompanyPage(entityId: string, value: unknown): string | null {
  if (!UUID.test(entityId)) return null;
  const own = serializeScope({ mode: 'one', id: entityId });
  return value === own ? null : own;
}
